<template>
  <div>
    <!-- page meta -->
    <Head>
      <title>Platforma | Integrations</title>
      <link
        rel="icon"
        :href="$filters.public_path('favicon.png')"
        type="favicon"
      />
      <meta
        name="description"
        content="Sell exclusive content your subscribers can't get anywhere else."
      />
    </Head>
  </div>
  <!-- content start -->

  <div class="relative mx-auto bg-transparent">
    <!-- ===================== HERO SECTION ===================== -->

    <section
      id="section1"
      class="top-0 left-0 -z-10 mx-auto flex h-[550px] w-full justify-center gap-1 bg-transparent bg-gradient-to-r from-[#FF0BE5] via-[#2D8CFF] to-[#2CFF4D] bg-no-repeat lg:h-[800px] lg:py-0"
    >
      <div
        class="integration-circles-hero mx-auto mt-0 w-[1200px] overflow-hidden bg-transparent bg-cover bg-[center_top_160px] bg-no-repeat px-[25px] pt-[106px] text-center lg:bg-[center_top_-140px] lg:pt-[150px] xl:px-0"
      >
        <h1
          class="top-full mt-0 mb-5 px-0 text-[35px] font-extrabold tracking-normal text-[#fff] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
        >
          Connect all your essential tools<br />
          to Platforma for free.
        </h1>

        <div class="lg:mx-[250px]">
          <p
            class="mt-[5px] mb-[25px] text-[15px] font-normal text-[#fff] lg:text-lg"
          >
            Welcome to the future of marketing, where useful features and
            integrations are shipped to you weekly.
          </p>
        </div>
      </div>
    </section>
  </div>

  <div class="mx-auto flex max-w-[1600px]">
    <div class="mx-0 w-full text-center">
      <h2 class="mt-[50px] text-[30px] font-bold text-[#292d34] lg:text-[40px]">
        Native integrations
      </h2>
      <p class="my-[15px] text-[16px] text-[#7c828d] lg:text-[19px]">
        Built by Platforma, loved by everyone
      </p>
    </div>
  </div>

  <!-- ========ROW 1======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path('/images/shopify-ecommerce-platforma-logo.png')
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Shopify
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Import your stores products to display in a Platforma membership area
          storefront page. Members can checkout and orders go direct to Shopify.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/stripe-payment-processing-integration-platforma.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Stripe
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Process payments using the #1 online merchant account. Lower
          transaction fees, and gain control over monetization.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 2======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="$filters.media_path('/images/drip-platforma-integration.png')"
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Drip
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          New members will be tagged and thrusted onto any email list in Drip.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path('/images/hubspot-platforma-integration.png')
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Hubspot
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Automate your email signup flow, and segment out your members to
          specific hubspot lists.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 3======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path('/images/sendinblue-platforma-integration.png')
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          SendInBlue
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Signups are instanlt imported into SendInBlue for autoresponding and
          remarketing.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path('/images/getresponse-platforma-integration.png')
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          GetResponse
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Create advanced email flows for remarketing via email or SMS.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 4======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/activecampaign-email-marketing-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          ActiveCampaign
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          The email marketing, marketing automation, and CRM tools you need to
          create incredible customer experiences.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/sendgrid-email-marketing-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          SendGrid
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Leverage the email service that customer-first brands trust for
          reliable inbox delivery at scale.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 5======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/mailerlite-email-marketing-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          MailerLite
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Digital marketing tools to grow your audience faster and drive revenue
          smarter.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/constantcontact-email-marketing-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Constant Contact
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Attract new customers and boost sales with their complete, easy-to-use
          digital marketing suite.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 6======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/mailchimp-email-marketing-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Mailchimp
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Win new customers with the #1 email marketing and automations brand*
          that recommends ways to get more opens, clicks, and sales.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/meta-facebook-ad-tracking-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Meta/Facebook Ads
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Save you Meta Pixel and create advanced remarketing campaigns for
          Facebook, Instagram, etc.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 7======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/google-ads-tracking-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Google Ads
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Send enhanced conversion to Google Ads using the Tag Manager container
          integration./p>
        </p>
      </div>
    </div>

    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/aweber-email-marketing-platforma-integrations.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          AWeber
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Create beautiful emails in half the time for every new members who is
          created in Platforma.
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 8======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path(
              '/images/OBS-platforma-streaming-integration.png'
            )
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          OBS
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Stream Live to members with the power and customization of the #1 used
          open-source streaming software.
        </p>
      </div>
    </div>

    <div
      class="m-[15px] rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="
            $filters.media_path('/images/zapier-platforma-integrations.png')
          "
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          Zapier
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Zapier gives you access to 1000+ apps with pre-existing and
          customizable integrations to help your team automate exactly what they
          need..
        </p>
      </div>
    </div>
  </div>

  <!-- ========ROW 9 ======== -->

  <div class="mx-auto max-w-[1600px] lg:flex">
    <div
      class="m-[15px] justify-between rounded-md border border-[#dadee6] py-[20px] lg:flex lg:w-[768px] lg:pr-[40px]"
    >
      <div class="m-auto p-5 lg:w-[52%]">
        <img
          :src="$filters.media_path('/images/platforma-api-integrations.png')"
          class="lazy w-[80%] transform cursor-pointer transition delay-100 duration-500 ease-in-out hover:scale-105 lg:w-auto"
          alt=""
        />
      </div>
      <div class="p-5 lg:w-[52%]">
        <h3 class="mt-[5px] mb-[18px] text-[18px] font-bold text-[#292d34]">
          API
        </h3>

        <p class="text-[13px] text-[#7c828d]">
          Build your own custom integrations and Platforma apps with our public
          API.
        </p>
      </div>
    </div>
  </div>
  <!-- ===================== CTA BLOCK ===================== -->
  <ProtectCtaBlock />
</template>

<!-- CONTENT END -->

<script>
import DefaultLayout from '../Layouts/DefaultLayout.vue';
import ButtonComponent from '@shared/Components/ButtonComponent.vue';
import Axios from 'axios';
import EmailCaptureHeaderWidget from '../Components/EmailCaptureHeader.vue';
import ProtectCtaBlock from '../Components/ProtectCtaBlockFooter.vue';

export default {
  layout: DefaultLayout,
  components: {
    ButtonComponent,
    EmailCaptureHeaderWidget,
    ProtectCtaBlock
  },

  computed: {
    backgroundImageStyleMain() {
      const imagePath = this.$filters.media_path(
        'images/platforma-header-background-eclipse.svg'
      );
      return {
        '--before-background-image': `url(${imagePath})`
      };
    }
  }
};
</script>

<style scoped>
.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  height: 1000px;
  width: 1700px;
  display: block;
  z-index: 0;
  transform: translateY(-42%) translateX(58%);
  background-repeat: no-repeat;
  background-image: var(--before-background-image);
}

.integration-circles-hero {
  background-image: url(../media/images/platforma-integrations-background.svg);
}
</style>
